import classNames from "classnames";
import { useGrowl } from "PFApp/use_growl";
import PageBox from "PFComponents/box/page_box";
import { Button } from "PFComponents/button";
import Checkbox from "PFComponents/checkbox/checkbox";
import SimplePagination from "PFComponents/pagination/simple_pagination";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import Toggle from "PFComponents/toggle/toggle";
import { parseResponseErrors } from "PFCore/helpers/parse_errors";
import {
  useCustomValuesCleanup,
  useCustomValuesInvalidate,
  useCustomValuesOrdinalUpdate
} from "PFCore/hooks/queries/custom_fields/admin";
import { useCustomTypeUpdate } from "PFCore/hooks/queries/custom_fields/use_custom_type_update";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import css from "./custom_values.module.scss";
import AddNewContent from "./parts/add_new_content";
import DraggableCustomValues from "./parts/draggable_custom_values";
import SetHierarchicalListModal from "./parts/set_hierarchical_list_modal";

const CustomValuesPage = ({ customType, customValues, setPage, setCustomValuesParams, isFetching }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [unapproved, setUnapproved] = useState(false);
  const [uncurated, setUncurated] = useState(false);
  const [editCF, setEditCF] = useState({});
  const [mergeList, setMergeList] = useState([]);
  const [selectedToMerge, setSelectedToMerge] = useState(null);
  const [showHierarchicalListModal, setShowHierarchicalListModal] = useState(false);
  const [customFieldsList, setCustomFieldsList] = useState([]);
  const [disableCleanup, setDisableCleanup] = useState(false);

  const [renderNewContent, setRenderNewContent] = useState(false);

  const { t } = useTranslation("admin", { keyPrefix: "customFields" });
  const growl = useGrowl();
  const history = useHistory();

  const { invalidate: invalidateCustomValues } = useCustomValuesInvalidate();
  const { updateCustomTypeOrderable } = useCustomTypeUpdate({
    onSuccess: () => {
      invalidateCustomValues();
      growl({ message: t("hierarchical.setListModal.success") });
    },
    onError: (response) => {
      let message = t("error");
      const errors = parseResponseErrors(response?.responseJSON?.errors);
      if (errors?.kind) {
        message = t("hierarchical.setListModal.singleKindError");
      }
      growl({ message, kind: "error" });
    }
  });

  const setOrdinalMutation = useCustomValuesOrdinalUpdate({
    onSuccess: () => growl({ message: t("hierarchical.saveHierarchy.success") }),
    onError: (response) => {
      let message = t("error");
      const errors = parseResponseErrors(response?.responseJSON?.errors);
      if (errors?.custom_value_ids) {
        message = t("hierarchical.saveHierarchy.tooManyError");
      }
      growl({ message, kind: "error" });
    }
  });

  const cleanupMutation = useCustomValuesCleanup({
    onSuccess: () => {
      setDisableCleanup(false);
      growl({ message: t("hierarchical.cleanup.success") });
    }
  });

  const { id, name, orderable: isHierarchical } = customType;

  const requiresApproval = customType.new_values === "require_approval";
  const requiresCuration = [
    "skills",
    "technical_experience",
    "brands",
    "company",
    "qualifications",
    "clients"
  ].includes(name);

  // to render seed button we need to check if the custom type is ranckable
  const isRankabled = !!customType.sections?.find(({ key }) => key === "rankable");

  useEffect(() => {
    customValues && customValues?.entries && setCustomFieldsList(customValues?.entries);
    setMergeList(customValues?.entries?.map(() => ({ renderMerge: false })));
  }, [customValues]);

  useEffect(() => {
    if (uncurated || unapproved || searchTerm) {
      setCustomValuesParams({ unapproved, uncurated, term: searchTerm });
      setPage(1);
    } else {
      setCustomValuesParams({});
    }
  }, [uncurated, unapproved, searchTerm, setCustomValuesParams, setPage]);

  const handleRemoveItem = () => setRenderNewContent(false);

  const handleCancelClick = () => history.push("/admin/custom_fields");

  const handleHierarchicalToggle = useCallback(
    (value) => {
      if (value) {
        setShowHierarchicalListModal(true);
      } else {
        updateCustomTypeOrderable.mutate({ id, orderable: false });
      }
    },
    [updateCustomTypeOrderable, id]
  );

  const handleOrderUpdate = useCallback(
    (values) =>
      setOrdinalMutation.mutate({
        customTypeId: id,
        customValueIds: values.map(({ id }) => id)
      }),
    [setOrdinalMutation, id]
  );

  const handleCleanupClick = useCallback(() => {
    setDisableCleanup(true);
    cleanupMutation.mutate({ customTypeId: id });
  }, [cleanupMutation, id]);

  const isLoading = setOrdinalMutation.isLoading || isFetching;

  return (
    <div className={css.wrapper}>
      <div className={css.header}>
        <h1>{t("customFieldContent")}</h1>
        <h3>{`(${name})`}</h3>
      </div>
      <div className={css.header}>
        <InputFieldSet
          label={t("translation:search")}
          lockedTip={t("translation:title")}
          value={searchTerm}
          qaId={`custom-field-search`}
          onChange={setSearchTerm}
          style={{ width: 300 }}
        />
        {requiresApproval && (
          <Checkbox label={t("unapprovedLabel")} checked={unapproved} handleChange={setUnapproved} />
        )}
        {requiresCuration && (
          <Checkbox label={t("uncuratedLabel")} checked={uncurated} handleChange={setUncurated} />
        )}
      </div>
      <PageBox style={{ padding: 20 }}>
        <div className={css.rowsWrapper}>
          <div className={classNames(css.valuesList, { [css.loading]: isLoading })}>
            <DraggableCustomValues
              isHierarchical={isHierarchical}
              cfId={id}
              editCF={editCF}
              setEditCF={setEditCF}
              mergeList={mergeList}
              setMergeList={setMergeList}
              selectedToMerge={selectedToMerge}
              setSelectedToMerge={setSelectedToMerge}
              requiresCuration={requiresCuration}
              requiresApproval={requiresApproval}
              isRankable={isRankabled}
              fields={customFieldsList}
              setFields={setCustomFieldsList}
              updateOrder={handleOrderUpdate}
            />
            {renderNewContent ? (
              <AddNewContent
                id={id}
                handleRemoveItem={handleRemoveItem}
                ordinal={isHierarchical ? customFieldsList.length : null}
                className={css.newContent}
              />
            ) : (
              <Button
                kind="secondary"
                onClick={() => setRenderNewContent(true)}
                disabled={renderNewContent}
                className={css.newContent}
              >
                {t("newContent")}
              </Button>
            )}
          </div>
          <div className={css.hierarchicalInfo}>
            <div>{t("hierarchical.hint")}</div>
            <Toggle
              disabled={updateCustomTypeOrderable.isLoading}
              checked={isHierarchical}
              description={t("hierarchical.toggle.title")}
              onChange={handleHierarchicalToggle}
              inline
            />
            <span>{t("hierarchical.toggle.description")}</span>
            {isHierarchical && (
              <div className={css.cleanupStep}>
                <div>{t("hierarchical.cleanupStep")}</div>
                <Button disabled={disableCleanup} onClick={handleCleanupClick}>
                  {t("translation:confirm")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </PageBox>
      <div className={css.buttons}>
        <Button kind="secondary" onClick={handleCancelClick}>
          {t("translation:cancel")}
        </Button>
      </div>
      <div className={css.pagination}>
        <SimplePagination
          currentPage={customValues?.meta?.page}
          totalPages={customValues?.meta?.totalPages}
          handleClick={setPage}
        />
      </div>
      {showHierarchicalListModal && (
        <SetHierarchicalListModal
          onClose={() => setShowHierarchicalListModal(false)}
          onSubmit={() => {
            updateCustomTypeOrderable.mutate({ id, orderable: true });
            setShowHierarchicalListModal(false);
          }}
        />
      )}
    </div>
  );
};

CustomValuesPage.propTypes = {
  customType: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    display_as: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    suggestion_description: PropTypes.string,
    value_type: PropTypes.string,
    kind: PropTypes.string,
    new_values: PropTypes.string,
    profile_completion_weight: PropTypes.number,
    sections: PropTypes.array,
    position: PropTypes.number,
    orderable: PropTypes.bool
  }).isRequired,
  customValues: PropTypes.shape({
    entries: PropTypes.arrayOf(
      PropTypes.shape({
        admin_approved: PropTypes.bool,
        blocked: PropTypes.bool,
        id: PropTypes.number,
        value: PropTypes.string
      })
    ),
    meta: PropTypes.shape({
      total: PropTypes.number,
      page: PropTypes.number,
      totalPages: PropTypes.number
    })
  }),
  setPage: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  setCustomValuesParams: PropTypes.func.isRequired
};

export default CustomValuesPage;
